import { useState, useEffect, useCallback, useMemo, useRef, type ReactNode } from "react";
import { captureError } from "@/lib/errorReporting";
import { usePostHog } from "posthog-js/react";
import { useParams, useNavigate } from "react-router-dom";
import { TopBar, Button, Icon, useToast } from "@/components/ui";
import { useRealtimeCabal } from "@/hooks/useRealtimeCabal";
import { useAuth } from "@/providers";
import LaunchStatusBanner from "@/components/LaunchStatusBanner";
// import TokenLockCard, { type TokenLockData } from "@/components/TokenLockCard";
import { type TokenLockData } from "@/components/TokenLockCard";
import McapProgressBar from "@/components/McapProgressBar";
// import FeesEarnedCard from "@/components/FeesEarnedCard";
import {
    estimateTokensForDeposit,
    formatTokenAmount,
    getCurveParams,
} from "@/utils/curveEstimator";
import { useConfig } from "@/providers";
import { useSolPrice } from "@/hooks/useSolPrice";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:8080";
const SOLANA_NETWORK = import.meta.env.VITE_SOLANA_NETWORK || "devnet";
const IS_DEVNET = SOLANA_NETWORK === "devnet";

/* ─── Types ─── */
interface CabalDetail {
    _id: string;
    name: string;
    ticker: string;
    slug: string;
    lore?: string;
    imageUrl?: string;
    status: "recruiting" | "launched" | "failed";
    depositTarget: number;
    depositCurrent: number;
    targetMcap: number;
    leaderWallet: string;
    leaderDeposit: number;
    memberCount: number;
    inviteCode: string;
    recruitingDeadline?: string;
    vaultAddress?: string;
    tokenMint?: string;
    curveEstimate?: {
        sqrtPrice: number;
        totalSolCommitted: number;
        totalTokensEstimated: number;
    };
}

interface TokenDetails {
    marketCap: string;
    marketCapSol: number;
    marketCapATH: string;
    holders: string;
    priceChange24h: string;
    graduationPct: number;
    tvl: string;
    aggregators: {
        axiom?: string;
        jupiter?: string;
        dexscreener?: string;
    };
    topVoices: Array<{
        handle: string;
        name: string;
        imageUrl?: string;
        tweetLink?: string;
        impressionCount?: number;
    }>;
    xViews: number;
    isMigrated?: boolean;
    priceUsd?: number;
}

interface RefundInfo {
    walletAddress: string;
    amount: number;
    txSignature: string;
}

/* ─── Countdown Hook ─── */
function useCountdown(deadline: string | undefined) {
    const [timeLeft, setTimeLeft] = useState<{
        hours: number;
        minutes: number;
        seconds: number;
        expired: boolean;
    }>({ hours: 0, minutes: 0, seconds: 0, expired: false });

    useEffect(() => {
        if (!deadline) return;

        function calc() {
            const ms = new Date(deadline!).getTime() - Date.now();
            if (ms <= 0) {
                setTimeLeft({ hours: 0, minutes: 0, seconds: 0, expired: true });
                return;
            }
            const h = Math.floor(ms / 3_600_000);
            const m = Math.floor((ms % 3_600_000) / 60_000);
            const s = Math.floor((ms % 60_000) / 1_000);
            setTimeLeft({ hours: h, minutes: m, seconds: s, expired: false });
        }

        calc();
        const interval = setInterval(calc, 1000);
        return () => clearInterval(interval);
    }, [deadline]);

    return timeLeft;
}

/* ════════════════════════════════════════════
   Active Cabal Detail Page
   ════════════════════════════════════════════ */
export default function ActiveCabalPage(): ReactNode {
    const { cabalId } = useParams<{ cabalId: string }>();
    const navigate = useNavigate();
    const { toast } = useToast();
    const posthog = usePostHog();
    const { token } = useAuth();
    const [cabal, setCabal] = useState<CabalDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [refunds, setRefunds] = useState<RefundInfo[]>([]);
    const [userLock, setUserLock] = useState<TokenLockData | null>(null);
    const [tokenDetails, setTokenDetails] = useState<TokenDetails | null>(null);
    const solPriceUsd = useSolPrice();

    // Real-time data from Firebase RTDB
    const realtime = useRealtimeCabal(cabal?.slug);

    // Use realtime deposit data if available, fallback to API data
    const liveDepositCurrent = realtime.depositProgress?.current ?? cabal?.depositCurrent ?? 0;
    const liveDepositTarget = realtime.depositProgress?.target ?? cabal?.depositTarget ?? 0;
    const liveStatus = realtime.status?.value ?? cabal?.status ?? "recruiting";
    const realtimeMcap = realtime.lockStatus?.currentMcap ?? 0;
    const liveMcap = tokenDetails?.marketCapSol || realtimeMcap;
    const liveLockStatus = realtime.lockStatus?.status ?? "none";
    const isTokensUnlocked = liveLockStatus === "unlocked" || liveLockStatus === "partially_unlocked";
    const isGraduated = realtime.graduation?.isGraduated || tokenDetails?.isMigrated || false;

    // Toast when tokens are actually distributed
    const prevLockStatus = useRef(liveLockStatus);
    useEffect(() => {
        if (!["unlocked", "partially_unlocked"].includes(prevLockStatus.current) && isTokensUnlocked) {
            toast("Tokens have been distributed to your wallet!", "success");
        }
        prevLockStatus.current = liveLockStatus;
    }, [liveLockStatus, isTokensUnlocked, toast]);

    const countdown = useCountdown(cabal?.recruitingDeadline);

    const [userDeposit, setUserDeposit] = useState(0);
    const [userEstimatedTokens, setUserEstimatedTokens] = useState(0);
    const [userFeesEarned, setUserFeesEarned] = useState(0);

    const fetchCabal = useCallback(() => {
        if (!cabalId) return;
        const controller = new AbortController();
        setLoading(true);

        fetch(`${API_BASE}/api/cabals/${cabalId}`, { signal: controller.signal })
            .then((r) => {
                if (!r.ok) throw new Error("Not found");
                return r.json();
            })
            .then((data) => {
                setCabal(data);
                if (data.status === "failed") {
                    fetchRefunds(data.slug);
                }
            })
            .catch((err) => {
                if (err.name !== "AbortError") captureError("Fetch error:", err);
            })
            .finally(() => setLoading(false));

        return () => controller.abort();
    }, [cabalId]);

    useEffect(() => {
        fetchCabal();
    }, [fetchCabal]);

    // Re-fetch cabal data when RTDB reports launched but we don't have tokenMint yet
    useEffect(() => {
        if (liveStatus !== "launched" || !cabalId || cabal?.tokenMint) return;

        function refetchCabal() {
            fetch(`${API_BASE}/api/cabals/${cabalId}`)
                .then((r) => (r.ok ? r.json() : null))
                .then((data) => {
                    if (!data) return;
                    setCabal(data);
                    if (data.tokenMint) {
                        fetch(`${API_BASE}/api/coins/${data.tokenMint}/details`)
                            .then((r) => (r.ok ? r.json() : null))
                            .then((details) => { if (details) setTokenDetails(details); })
                            .catch(() => { });
                    }
                })
                .catch(() => { });
        }

        refetchCabal();
        const interval = setInterval(refetchCabal, 3_000);
        return () => clearInterval(interval);
    }, [liveStatus, cabalId, cabal?.tokenMint]);

    // Fetch user's deposits
    useEffect(() => {
        if (!cabal || !token) return;
        fetch(`${API_BASE}/api/cabals/${cabal.slug}/my-deposits`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then((r) => (r.ok ? r.json() : { total: 0, estimatedTokens: 0 }))
            .then((data) => {
                setUserDeposit(data.total || 0);
                setUserEstimatedTokens(data.estimatedTokens || 0);
                setUserFeesEarned(data.feesEarned || 0);
            })
            .catch(() => {
                setUserDeposit(0);
                setUserEstimatedTokens(0);
                setUserFeesEarned(0);
            });
    }, [cabal?.slug, token, isGraduated]);

    // Fetch user's token lock
    useEffect(() => {
        if (!cabal || !token || (liveStatus !== "launched" && cabal.status !== "launched")) return;
        fetch(`${API_BASE}/api/cabals/${cabal.slug}/locks/me`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then((r) => (r.ok ? r.json() : { lock: null }))
            .then((data) => setUserLock(data.lock || null))
            .catch(() => setUserLock(null));
    }, [cabal?.slug, liveStatus, cabal?.status, token, liveLockStatus]);

    // Fetch token details when launched
    useEffect(() => {
        if (!cabal || (liveStatus !== "launched" && cabal.status !== "launched") || !cabal.tokenMint) return;

        function fetchDetails() {
            fetch(`${API_BASE}/api/coins/${cabal!.tokenMint!}/details`)
                .then((r) => (r.ok ? r.json() : null))
                .then((data) => { if (data) setTokenDetails(data); })
                .catch(() => { });
        }

        fetchDetails();
        const interval = setInterval(fetchDetails, 15_000);
        return () => clearInterval(interval);
    }, [cabal?.slug, liveStatus, cabal?.status, cabal?.tokenMint]);

    function fetchRefunds(slug: string) {
        if (!token) return;
        fetch(`${API_BASE}/api/cabals/${slug}/my-refunds`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then((r) => (r.ok ? r.json() : { refunds: [] }))
            .then((data) => setRefunds(data.refunds || []))
            .catch(() => setRefunds([]));
    }

    const poolShare = liveDepositTarget > 0
        ? Math.round((userDeposit / liveDepositTarget) * 100)
        : 0;

    const subtitleText = loading || !cabal
        ? undefined
        : liveStatus === "failed"
            ? "Launch Failed"
            : liveStatus === "launched"
                ? "Coin Launched"
                : "Codev in Progress";

    return (
        <div className="relative flex min-h-screen w-full flex-col overflow-hidden max-w-[430px] mx-auto border-x border-white/5 shadow-2xl bg-[#0a0a0a]">
            <TopBar
                variant="minimal"
                showBack
                onBack={() => navigate(-1)}
                title={subtitleText}
            />

            <main className={`relative z-10 flex-1 flex flex-col px-5 pt-[clamp(4px,0.6dvh,8px)] overflow-y-auto ${liveStatus === "recruiting" || liveStatus === "failed" ? "pb-28" : "pb-[clamp(16px,2.6dvh,24px)]"}`}>
                {loading ? (
                    <div className="space-y-4 animate-pulse">
                        {/* Coin Header skeleton */}
                        <div className="flex flex-col items-center pt-[clamp(4px,0.6dvh,8px)] mb-[clamp(16px,2.6dvh,24px)]">
                            <div className="flex items-center gap-4">
                                <div className="flex items-center gap-3">
                                    <div className="size-[clamp(44px,6dvh,56px)] rounded-full bg-white/5" />
                                    <div className="h-[36px] w-[100px] bg-white/5 rounded" />
                                </div>
                                <div className="h-5 w-12 bg-white/5 rounded-[4px]" />
                            </div>
                        </div>
                        {/* Contribution card skeleton */}
                        <div
                            className="rounded-[12px] px-4 py-[clamp(12px,1.7dvh,16px)]"
                            style={{ background: "rgba(255,255,255,0.05)", border: "0.5px solid rgba(255,255,255,0.25)" }}
                        >
                            <div className="flex items-center gap-2 mb-[clamp(12px,1.7dvh,16px)]">
                                <div className="size-4 bg-white/5 rounded" />
                                <div className="h-3.5 w-28 bg-white/5 rounded" />
                            </div>
                            <div className="flex items-start justify-between">
                                <div className="flex flex-col gap-[5px] items-center w-[66px]">
                                    <div className="h-2.5 w-14 bg-white/5 rounded" />
                                    <div className="h-4 w-12 bg-white/5 rounded" />
                                </div>
                                <div className="w-px self-stretch bg-white/10" />
                                <div className="flex flex-col gap-[5px] items-center w-[66px]">
                                    <div className="h-2.5 w-14 bg-white/5 rounded" />
                                    <div className="h-4 w-8 bg-white/5 rounded" />
                                </div>
                                <div className="w-px self-stretch bg-white/10" />
                                <div className="flex flex-col gap-[5px] items-center w-[66px]">
                                    <div className="h-2.5 w-14 bg-white/5 rounded" />
                                    <div className="h-4 w-12 bg-white/5 rounded" />
                                </div>
                            </div>
                        </div>
                        {/* Deposit Progress skeleton */}
                        <div
                            className="rounded-[12px] px-4 py-[clamp(12px,1.7dvh,16px)]"
                            style={{
                                background: "linear-gradient(to bottom, rgba(115,115,115,0), rgba(115,115,115,0.2))",
                                border: "0.5px solid rgba(153,153,153,0.5)",
                            }}
                        >
                            <div className="flex items-start justify-between mb-[clamp(12px,1.7dvh,20px)]">
                                <div className="flex flex-col gap-[6px]">
                                    <div className="h-3.5 w-24 bg-white/5 rounded" />
                                    <div className="h-4 w-32 bg-white/5 rounded" />
                                </div>
                                <div className="flex flex-col items-end gap-[6px]">
                                    <div className="h-3.5 w-16 bg-white/5 rounded" />
                                    <div className="h-4 w-24 bg-white/5 rounded" />
                                </div>
                            </div>
                            <div className="h-2 w-full bg-[rgba(255,255,255,0.08)] rounded-full" />
                            <div className="flex justify-between mt-[clamp(8px,1dvh,12px)]">
                                <div className="h-2.5 w-16 bg-white/5 rounded" />
                                <div className="h-2.5 w-24 bg-white/5 rounded" />
                            </div>
                        </div>
                    </div>
                ) : !cabal ? (
                    <div className="flex-1 flex items-center justify-center">
                        <p className="text-white/40 font-[family-name:var(--font-display)]">CODEV not found</p>
                    </div>
                ) : liveStatus === "failed" ? (
                    /* ═══ FAILED STATE ═══ */
                    <FailedState
                        cabal={cabal}
                        realtime={realtime}
                        refunds={refunds}
                        userDeposit={userDeposit}
                        tokenDetails={tokenDetails}
                        isGraduated={isGraduated}
                    />
                ) : (
                    /* ═══ NORMAL RECRUITING / LAUNCHED STATE ═══ */
                    <>
                        <CoinHeader cabal={cabal} status={liveStatus} aggregators={tokenDetails?.aggregators} isGraduated={isGraduated} />

                        {cabal.lore && liveStatus === "recruiting" && <LoreCard lore={cabal.lore} />}

                        {(liveStatus === "launched" || cabal.status === "launched") && (
                            <LaunchStatusBanner
                                cabalSlug={cabal.slug}
                                tokenMint={cabal.tokenMint}
                            />
                        )}

                        {tokenDetails && (
                            <TokenStatsGrid details={tokenDetails} />
                        )}

                        {userDeposit > 0 && (
                            <ContributionCard
                                deposit={userDeposit}
                                poolShare={poolShare}
                                estimatedTokens={userEstimatedTokens}
                                actualLockedAmount={userLock?.lockedAmount}
                                launched={liveStatus === "launched" || cabal.status === "launched"}
                                lockStatus={userLock?.status}
                                unlocksAt={userLock?.unlocksAt}
                                graduationPct={tokenDetails?.graduationPct}
                                currentMcap={liveMcap}
                                priceChange24h={tokenDetails?.priceChange24h}
                                realtimeMcap={realtimeMcap}
                                isGraduated={isGraduated}
                                priceUsd={tokenDetails?.priceUsd}
                                solPriceUsd={solPriceUsd ?? undefined}
                                feesEarned={userFeesEarned}
                            />
                        )}

                        {/* Fees earned now shown inside ContributionCard */}

                        {liveStatus === "recruiting" && (
                            <DepositProgress
                                depositCurrent={liveDepositCurrent}
                                depositTarget={liveDepositTarget}
                                countdown={countdown}
                            />
                        )}

                        {/* {tokenDetails && tokenDetails.topVoices.length > 0 && (
                            <TokenVoicesSection voices={tokenDetails.topVoices} />
                        )} */}
                    </>
                )}
            </main>

            {/* ─── Bottom CTA ─── */}
            {cabal && liveStatus === "recruiting" && (
                <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[430px] px-5 py-3 bg-[#0a0a0a]/95 backdrop-blur-xl border-t border-white/5 z-50">
                    <Button
                        variant="cta"
                        size="lg"
                        textStyle="v2"
                        fullWidth
                        onClick={() => {
                            const url = `${window.location.origin}/codevs/join/${cabal.inviteCode}`;
                            const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
                            const useNativeShare = isMobile && typeof navigator.share === "function";
                            posthog.capture("invite_shared", {
                                cabalSlug: cabal.slug,
                                ticker: cabal.ticker,
                                method: useNativeShare ? "native_share" : "clipboard",
                            });
                            if (useNativeShare) {
                                navigator.share({ title: `Join ${cabal.ticker} Cabal`, url }).catch(() => { });
                            } else {
                                navigator.clipboard.writeText(url).then(
                                    () => toast("Invite link copied to clipboard!", "success"),
                                    () => toast("Failed to copy link", "error"),
                                );
                            }
                        }}
                    >
                        <Icon name="share" size={20} />
                        Share Link
                    </Button>
                </div>
            )}

            {cabal && liveStatus === "failed" && (
                <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[430px] px-5 py-3 bg-[#0a0a0a]/95 backdrop-blur-xl border-t border-white/5 z-50">
                    <Button
                        variant="cta"
                        size="lg"
                        textStyle="v2"
                        fullWidth
                        onClick={() => navigate("/codevs")}
                    >
                        View My CODEVs
                    </Button>
                </div>
            )}
        </div>
    );
}

/* ─── Failed State ─── */
function FailedState({
    cabal,
    realtime,
    refunds,
    userDeposit,
    tokenDetails,
    isGraduated,
}: {
    cabal: CabalDetail;
    realtime: ReturnType<typeof useRealtimeCabal>;
    refunds: RefundInfo[];
    userDeposit: number;
    tokenDetails: TokenDetails | null;
    isGraduated: boolean;
}): ReactNode {
    return (
        <>
            <CoinHeader cabal={cabal} status="failed" aggregators={tokenDetails?.aggregators} isGraduated={isGraduated} />

            {/* Failed Hero */}
            <div className="mb-4">
                <div className="bg-red-500/5 border-[0.5px] border-red-500/20 rounded-[12px] p-4 text-center relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-red-500/5 to-transparent" />
                    <div className="relative">
                        <div className="size-[clamp(36px,5.1dvh,48px)] mx-auto mb-[clamp(8px,1dvh,12px)] rounded-full bg-red-500/10 flex items-center justify-center">
                            <Icon name="cancel" size={30} />
                        </div>
                        <h2 className="text-lg font-bold text-red-400 mb-2 font-[family-name:var(--font-display)]">
                            Launch Failed
                        </h2>
                        <p className="text-white/50 text-xs font-[family-name:var(--font-display)]">
                            {realtime.status?.reason
                                ? <>Token launch failed: <span className="font-semibold text-white/70">{realtime.status.reason}</span>. All deposits have been refunded.</>
                                : <>The <span className="font-semibold text-white/70">{cabal.depositTarget} SOL</span> deposit target was not met. All deposits have been refunded.</>
                            }
                        </p>
                    </div>
                </div>
            </div>

            {/* Deposit Summary */}
            <div className="bg-[rgba(255,255,255,0.05)] border-[0.5px] border-[rgba(255,255,255,0.25)] rounded-[12px] p-4 mb-3">
                <div className="flex justify-between items-center">
                    <span className="text-[10px] font-normal text-white/50 font-[family-name:var(--font-display)]">Final Deposits</span>
                    <span className="text-sm font-medium text-white font-[family-name:var(--font-display)] tracking-[-0.32px]">
                        {parseFloat(cabal.depositCurrent.toFixed(2))} / {parseFloat(cabal.depositTarget.toFixed(2))} SOL
                    </span>
                </div>
            </div>

            {/* Refund Transactions */}
            {refunds.length > 0 && (
                <div className="mb-6">
                    <p className="text-[10px] font-semibold text-white/50 mb-3 px-1 font-[family-name:var(--font-display)]">
                        Refund Transactions
                    </p>
                    <div className="space-y-2">
                        {refunds.map((r) => (
                            <a
                                key={r.txSignature}
                                href={`https://solscan.io/tx/${r.txSignature}${IS_DEVNET ? "?cluster=devnet" : ""}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="bg-gradient-to-b from-[rgba(115,115,115,0)] to-[rgba(115,115,115,0.1)] border-[0.5px] border-[rgba(153,153,153,0.5)] rounded-[12px] p-3 flex items-center justify-between hover:to-[rgba(115,115,115,0.2)] transition-colors group block"
                            >
                                <div className="flex items-center gap-2">
                                    <Icon name="receipt" size={12} className="opacity-50" />
                                    <div>
                                        <p className="text-[10px] font-semibold text-white/50 font-[family-name:var(--font-display)]">
                                            Refund — {r.amount.toFixed(4)} SOL
                                        </p>
                                        <p className="text-xs font-mono text-white/60">
                                            {r.txSignature.slice(0, 8)}...{r.txSignature.slice(-8)}
                                        </p>
                                    </div>
                                </div>
                                <Icon name="external-link" size={16} className="opacity-30 group-hover:opacity-60 transition-opacity" />
                            </a>
                        ))}
                    </div>
                </div>
            )}

            {userDeposit > 0 && (
                <div className="bg-[#f3ce28]/5 border-[0.5px] border-[#f3ce28]/20 rounded-[12px] p-3 mb-4 flex items-center gap-2">
                    <Icon name="check-circle" size={16} className="opacity-80" />
                    <p className="text-xs text-[#f3ce28]/80 font-[family-name:var(--font-display)]">
                        Your <span className="font-semibold">{parseFloat(userDeposit.toFixed(2))} SOL</span> has been refunded to your wallet.
                    </p>
                </div>
            )}
        </>
    );
}

/* ─── Coin Header ─── */
function CoinHeader({ cabal, status, aggregators, isGraduated }: {
    cabal: CabalDetail;
    status: string;
    aggregators?: TokenDetails["aggregators"];
    isGraduated?: boolean;
}): ReactNode {
    const { ANONCOIN_BASE_URL } = useConfig();
    const { toast } = useToast();
    const statusLabel = isGraduated ? "Graduated" : status === "launched" ? "LIVE" : status === "failed" ? "Failed" : "LIVE";
    const statusColor = status === "failed"
        ? "bg-red-500/10 text-red-400"
        : "bg-[rgba(255,255,255,0.09)] text-white";

    const tradeLinks = [
        { name: "Axiom", icon: "/icons/axiom.png", url: aggregators?.axiom },
        { name: "Jupiter", icon: "/icons/jupiter.png", url: aggregators?.jupiter },
        { name: "Dexscreener", icon: "/icons/dexscreener.png", url: aggregators?.dexscreener },
    ].filter((l) => l.url);

    return (
        <div className="flex flex-col items-center mb-[clamp(16px,2.6dvh,24px)] pt-[clamp(4px,0.6dvh,8px)]">
            {/* Token image + ticker + badge */}
            <div className="flex items-center gap-4">
                <div className="flex items-center gap-3">
                    {/* Token Avatar */}
                    <div className="size-[clamp(44px,6dvh,56px)] rounded-full overflow-hidden border border-white/10 bg-[#0a0a0a]">
                        {cabal.imageUrl ? (
                            <img alt={cabal.ticker} className="w-full h-full object-contain" src={cabal.imageUrl} />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center">
                                <span className="text-2xl font-extrabold text-white font-[family-name:var(--font-display)]">
                                    {cabal.ticker.toUpperCase().charAt(0)}
                                </span>
                            </div>
                        )}
                    </div>
                    <p className="text-[clamp(24px,3dvh,28px)] font-extrabold text-white tracking-[-0.84px] leading-[1.15] font-[family-name:var(--font-display)] truncate max-w-[200px]">
                        {cabal.ticker.toUpperCase()}
                    </p>
                </div>
                <div className={`px-2.5 py-1 rounded-[4px] ${statusColor}`}>
                    <p className="text-[12px] font-medium leading-[1.15] font-[family-name:var(--font-display)]">
                        {statusLabel}
                    </p>
                </div>
            </div>

            {/* Trade links (after launch) */}
            {status === "launched" && (
                <div className="flex items-center gap-2 my-6">
                    <a
                        href={`${ANONCOIN_BASE_URL}/${cabal.tokenMint}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-8 h-8 rounded flex items-center justify-center hover:opacity-80 transition-opacity"
                        title="Anoncoin"
                    >
                        <img alt="Anoncoin" src="/icons/anoncoin.png" className="w-8 h-8 object-contain" />
                    </a>
                    {tradeLinks.map((l) => (
                        <a
                            key={l.name}
                            href={l.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-8 h-8 rounded flex items-center justify-center hover:opacity-80 transition-opacity"
                            title={l.name}
                        >
                            <img alt={l.name} src={l.icon} className="w-6 h-6 object-contain" />
                        </a>
                    ))}

                    {/* Contract address */}
                    {cabal.tokenMint && (
                        <>
                            <div className="w-px h-3.5 bg-white/10" />
                            <button
                                className="flex items-center gap-2 group"
                                onClick={() => {
                                    navigator.clipboard.writeText(cabal.tokenMint!).then(
                                        () => toast("Contract address copied!", "success"),
                                        () => toast("Failed to copy", "error"),
                                    );
                                }}
                            >
                                <span className="text-[14px] font-mono text-white/30 group-hover:text-white/50 transition-colors">
                                    {cabal.tokenMint.slice(0, 4)}...{cabal.tokenMint.slice(-4)}
                                </span>
                                <Icon name="copy" size={14} className="opacity-30 group-hover:opacity-60 transition-opacity" />
                            </button>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

/* ─── Token Stats Grid (2x2) ─── */
function TokenStatsGrid({ details }: { details: TokenDetails }): ReactNode {
    const rawChange = details.priceChange24h || "0%";
    const changeNum = parseFloat(rawChange.replace("%", "")) || 0;
    const change = `${changeNum >= 0 ? "+" : ""}${rawChange}`;

    const stats = [
        { label: "MCAP", value: details.marketCap, icon: "cash"/*, subtitle: change, subtitleChangeNum: changeNum*/ },
        { label: "24H Change", value: change, icon: "trendline-up", changeNum },
    ];

    return (
        <div className="flex gap-[clamp(6px,0.8dvh,8px)] mb-4">
            {stats.map((s) => (
                <StatCard key={s.label} {...s} />
            ))}
        </div>
    );
}

function StatCard({ label, value, icon, changeNum, subtitle, subtitleChangeNum }: {
    label: string;
    value: string;
    icon: string;
    changeNum?: number;
    subtitle?: string;
    subtitleChangeNum?: number;
}): ReactNode {
    return (
        <div className="flex-1 bg-gradient-to-b from-[rgba(115,115,115,0)] to-[rgba(115,115,115,0.2)] border-[0.5px] border-[rgba(153,153,153,0.5)] rounded-[12px] p-[clamp(8px,1.3dvh,16px)] flex items-center gap-[clamp(6px,1dvh,12px)]">
            <div className="bg-gradient-to-b from-[rgba(115,115,115,0)] to-[rgba(115,115,115,0.5)] border border-[rgba(255,255,255,0.2)] rounded-full size-[clamp(32px,3.9dvh,44px)] flex items-center justify-center opacity-30 shrink-0">
                <Icon name={icon} size={18} />
            </div>
            <div className="flex flex-col gap-[clamp(2px,0.4dvh,4px)] min-w-0">
                <p className="text-[14px] font-medium text-white/40 tracking-[-0.28px] leading-[1.15] whitespace-nowrap font-[family-name:var(--font-display)]">
                    {label}
                </p>
                <div className="flex items-baseline gap-1.5 min-w-0">
                    <p className={`text-[16px] font-medium tracking-[-0.32px] leading-[1.15] truncate font-[family-name:var(--font-display)] ${changeNum !== undefined
                        ? changeNum >= 0 ? "text-[#4ade80]" : "text-red-400"
                        : "text-white"
                        }`}>
                        {value}
                    </p>
                    {subtitle && (
                        <span className="text-[11px] font-medium tracking-[-0.22px] leading-[1.15] shrink-0 font-[family-name:var(--font-display)]">
                            <span className={subtitleChangeNum !== undefined
                                ? subtitleChangeNum >= 0 ? "text-[#4ade80]/70" : "text-red-400/70"
                                : "text-white/40"
                            }>{subtitle}</span>
                            <span className="text-white/40"> 24H</span>
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
}

/* ─── Lore Card ─── */
function LoreCard({ lore }: { lore: string }): ReactNode {
    return (
        <div className="mb-4">
            <div className="bg-[rgba(255,255,255,0.05)] border-[0.5px] border-[rgba(255,255,255,0.15)] rounded-[12px] p-4">
                <p className="text-white/60 text-[12px] font-[family-name:var(--font-display)] leading-relaxed">
                    <span className="text-[#f3ce28] font-semibold">Lore:</span> {lore}
                </p>
            </div>
        </div>
    );
}

/* ─── Your Contribution ─── */
function ContributionCard({
    deposit,
    poolShare,
    estimatedTokens,
    actualLockedAmount,
    launched,
    lockStatus,
    unlocksAt,
    graduationPct,
    currentMcap,
    priceChange24h,
    realtimeMcap,
    isGraduated,
    priceUsd,
    solPriceUsd,
    feesEarned,
}: {
    deposit: number;
    poolShare: number;
    estimatedTokens: number;
    actualLockedAmount?: string;
    launched?: boolean;
    lockStatus?: "locked" | "unlocked" | "withdrawn" | "failed";
    unlocksAt?: string;
    graduationPct?: number;
    currentMcap?: number;
    priceChange24h?: string;
    realtimeMcap?: number;
    isGraduated?: boolean;
    priceUsd?: number;
    solPriceUsd?: number;
    feesEarned?: number;

}): ReactNode {
    const { INITIAL_MC_SOL, MIGRATION_MC_SOL, TOTAL_TOKEN_SUPPLY } = useConfig();
    const curveParams = useMemo(() => getCurveParams(INITIAL_MC_SOL, MIGRATION_MC_SOL, TOTAL_TOKEN_SUPPLY), [INITIAL_MC_SOL, MIGRATION_MC_SOL, TOTAL_TOKEN_SUPPLY]);
    const hasActual = !!actualLockedAmount && Number(actualLockedAmount) > 0;
    const tokensOut = hasActual
        ? Number(actualLockedAmount) / 1e9
        : estimatedTokens > 0
            ? estimatedTokens
            : estimateTokensForDeposit(deposit, curveParams.sqrtStartPrice, curveParams, 0).tokensOut;
    const allocationLabel = hasActual ? "Token Received" : "EST. Allocation";
    const allocationDisplay = formatTokenAmount(tokensOut);

    const dollarValue = priceUsd && priceUsd > 0 && tokensOut > 0
        ? priceUsd * tokensOut
        : undefined;
    const depositUsdValue = solPriceUsd && solPriceUsd > 0
        ? deposit * solPriceUsd
        : undefined;
    const formatUsd = (val: number) => {
        if (val >= 1_000_000) return `$${(val / 1_000_000).toFixed(2)}M`;
        if (val >= 1_000) return `$${(val / 1_000).toFixed(2)}K`;
        if (val >= 1) return `$${val.toFixed(2)}`;
        return `$${val.toFixed(4)}`;
    };

    const isLocked = launched && lockStatus === "locked" && !isGraduated;

    const [unlockCountdown, setUnlockCountdown] = useState<{ text: string; expired: boolean }>({
        text: "",
        expired: false,
    });

    useEffect(() => {
        if (!unlocksAt || !isLocked) return;

        function calc() {
            const ms = new Date(unlocksAt!).getTime() - Date.now();
            if (ms <= 0) {
                setUnlockCountdown({ text: "", expired: true });
                return;
            }
            const h = Math.floor(ms / 3_600_000);
            const m = Math.floor((ms % 3_600_000) / 60_000);
            const s = Math.floor((ms % 60_000) / 1_000);
            const pad = (n: number) => String(n).padStart(2, "0");
            setUnlockCountdown({
                text: h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`,
                expired: false,
            });
        }

        calc();
        const interval = setInterval(calc, 1000);
        return () => clearInterval(interval);
    }, [unlocksAt, isLocked]);

    return (
        <div className="mb-4">
            <div className="bg-[rgba(255,255,255,0.05)] border-[0.5px] border-[rgba(255,255,255,0.25)] rounded-[12px] px-4 py-[clamp(12px,1.7dvh,16px)] overflow-hidden">
                {/* Header */}
                <div className="flex items-center gap-2 mb-[clamp(12px,1.7dvh,16px)]">
                    <Icon name="wallet" size={16} />
                    <p className="text-[13px] font-semibold text-white font-[family-name:var(--font-display)]">
                        Your Contribution
                    </p>
                </div>

                {/* Stats row */}
                {launched ? (
                    <div className="flex items-start justify-between">
                        <div className="flex flex-col gap-[5px] items-center flex-1">
                            <p className="text-[10px] font-normal text-white/50 text-center tracking-[-0.2px] leading-[1.15] font-[family-name:var(--font-display)]">
                                Deposited
                            </p>
                            <p className="text-[16px] font-medium text-white tracking-[-0.64px] leading-[1.15] font-[family-name:var(--font-display)]">
                                {parseFloat(deposit.toFixed(2))} SOL
                            </p>
                            {depositUsdValue !== undefined && (
                                <p className="text-[11px] font-medium text-white/40 tracking-[-0.22px] leading-[1.15] font-[family-name:var(--font-display)]">
                                    {formatUsd(depositUsdValue)}
                                </p>
                            )}
                        </div>
                        <div className="w-px self-stretch bg-white/10" />
                        <div className="flex flex-col gap-[5px] items-center flex-1">
                            <div className="flex items-center gap-1.5">
                                <p className="text-[10px] font-normal text-white/50 text-center tracking-[-0.2px] leading-[1.15] font-[family-name:var(--font-display)]">
                                    {allocationLabel}
                                </p>
                                {isLocked && (
                                    <span className="text-[8px] font-semibold text-[#f3ce28] bg-[#f3ce28]/10 px-1.5 py-0.5 rounded-[3px] leading-[1] font-[family-name:var(--font-display)]">
                                        LOCKED
                                    </span>
                                )}
                            </div>
                            <p className="text-[16px] font-medium text-white tracking-[-0.64px] leading-[1.15] font-[family-name:var(--font-display)]">
                                {allocationDisplay}
                            </p>
                            {dollarValue !== undefined && (
                                <p className="text-[11px] font-medium text-white/40 tracking-[-0.22px] leading-[1.15] font-[family-name:var(--font-display)]">
                                    {formatUsd(dollarValue)}
                                </p>
                            )}
                        </div>
                        {launched && isGraduated && feesEarned !== undefined && (
                            <>
                                <div className="w-px self-stretch bg-white/10" />
                                <div className="flex flex-col gap-[5px] items-center flex-1">
                                    <p className="text-[10px] font-normal text-white/50 text-center tracking-[-0.2px] leading-[1.15] font-[family-name:var(--font-display)]">
                                        Fees Earned
                                    </p>
                                    <p className="text-[16px] font-medium text-white tracking-[-0.64px] leading-[1.15] font-[family-name:var(--font-display)]">
                                        {feesEarned.toFixed(3)} SOL
                                    </p>
                                    {solPriceUsd && feesEarned > 0.001 && (
                                        <p className="text-[11px] font-medium text-white/40 tracking-[-0.22px] leading-[1.15] font-[family-name:var(--font-display)]">
                                            ~{formatUsd(feesEarned * solPriceUsd)}
                                        </p>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                ) : (
                    <div className="flex items-start justify-between">
                        <div className="flex flex-col gap-[5px] items-center w-[66px]">
                            <p className="text-[10px] font-normal text-white/50 text-center tracking-[-0.2px] leading-[1.15] font-[family-name:var(--font-display)]">
                                My Deposit
                            </p>
                            <p className="text-[16px] font-medium text-white tracking-[-0.64px] leading-[1.15] font-[family-name:var(--font-display)]">
                                {parseFloat(deposit.toFixed(2))} SOL
                            </p>
                            {depositUsdValue !== undefined && (
                                <p className="text-[11px] font-medium text-white/40 tracking-[-0.22px] leading-[1.15] font-[family-name:var(--font-display)]">
                                    ~{formatUsd(depositUsdValue)}
                                </p>
                            )}
                        </div>
                        <div className="w-px self-stretch bg-white/10" />
                        <div className="flex flex-col gap-[4px] items-center w-[66px]">
                            <p className="text-[10px] font-normal text-white/50 text-center tracking-[-0.2px] leading-[1.15] font-[family-name:var(--font-display)]">
                                Pool Share
                            </p>
                            <p className="text-[16px] font-medium text-white tracking-[-0.64px] leading-[1.15] font-[family-name:var(--font-display)]">
                                {poolShare}%
                            </p>
                        </div>
                        <div className="w-px self-stretch bg-white/10" />
                        <div className="flex flex-col gap-[5px] items-center w-[66px]">
                            <p className="text-[10px] font-normal text-white/50 text-center tracking-[-0.2px] leading-[1.15] font-[family-name:var(--font-display)]">
                                {allocationLabel}
                            </p>
                            <p className="text-[16px] font-medium text-[#f3ce28] tracking-[-0.64px] leading-[1.15] font-[family-name:var(--font-display)]">
                                {allocationDisplay}
                            </p>
                        </div>
                    </div>
                )}

                {/* Unlock info label */}
                {launched && !isGraduated && isLocked && (
                    <p className="text-[10px] font-normal text-white/40 mt-3 px-1 font-[family-name:var(--font-display)]">
                        Tokens unlock when bonding curve reaches 100% or time expires
                    </p>
                )}

                {/* Fee earning info label */}
                {launched && !isGraduated && (
                    <p className="text-[10px] font-normal text-white/40 mt-3 px-1 font-[family-name:var(--font-display)] text-center">
                        Start earning fee once bonding curve reaches 100%
                    </p>
                )}


                {/* MCAP Progress (launched, not graduated) */}
                {launched && !isGraduated && (
                    graduationPct !== undefined ? (
                        <div className="mt-3">
                            <McapProgressBar
                                graduationPct={graduationPct}
                                currentMcap={currentMcap}
                                priceChange24h={priceChange24h}
                            />
                        </div>
                    ) : (realtimeMcap ?? 0) > 0 ? (
                        <div className="mt-3">
                            <McapProgressBar
                                graduationPct={0}
                                currentMcap={realtimeMcap}
                            />
                        </div>
                    ) : null
                )}

                {/* Unlock countdown (launched + locked) */}
                {isLocked && unlocksAt && (
                    <div className="mt-3">
                        {unlockCountdown.expired ? (
                            <div className="bg-[#f3ce28]/5 border-[0.5px] border-[#f3ce28]/20 rounded-[8px] p-3 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Icon name="spinner" size={18} spinning />
                                    <span className="text-[12px] text-[#f3ce28]/80 font-[family-name:var(--font-display)]">
                                        Distributing tokens...
                                    </span>
                                </div>
                            </div>
                        ) : (
                            <div className="bg-[rgba(255,255,255,0.05)] border-[0.5px] border-[rgba(255,255,255,0.1)] rounded-[8px] p-3 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Icon name="schedule" size={18} className="animate-pulse" />
                                    <span className="text-[12px] text-white/60 font-[family-name:var(--font-display)]">
                                        Time Left
                                    </span>
                                </div>
                                <span className="text-[14px] font-semibold text-[#f3ce28] tabular-nums font-[family-name:var(--font-display)]">
                                    {unlockCountdown.text}
                                </span>
                            </div>
                        )}
                    </div>
                )}
            </div>
            {!launched && (
                <p className="text-[10px] text-white/40 mt-2 px-1 font-[family-name:var(--font-display)]">
                    Note: If the deposit threshold is not met, all tokens are trustlessly returned.
                </p>
            )}
        </div>
    );
}

/* ─── Deposit Progress (recruiting) ─── */
function DepositProgress({
    depositCurrent,
    depositTarget,
    countdown,
}: {
    depositCurrent: number;
    depositTarget: number;
    countdown: { hours: number; minutes: number; seconds: number; expired: boolean };
}): ReactNode {
    const progress = depositTarget > 0
        ? Math.min((depositCurrent / depositTarget) * 100, 100)
        : 0;

    const pad = (n: number) => String(n).padStart(2, "0");
    const timeDisplay = countdown.expired
        ? "Resolving..."
        : `${pad(countdown.hours)}h ${pad(countdown.minutes)}m ${pad(countdown.seconds)}s`;

    return (
        <div className="mb-4">
            <div className="bg-gradient-to-b from-[rgba(115,115,115,0)] to-[rgba(115,115,115,0.2)] border-[0.5px] border-[rgba(153,153,153,0.5)] rounded-[12px] px-4 py-[clamp(12px,1.7dvh,16px)] overflow-hidden">
                {/* Header */}
                <div className="flex items-start justify-between mb-[clamp(12px,1.7dvh,20px)]">
                    <div className="flex flex-col gap-1">
                        <p className="text-[13px] font-semibold text-white font-[family-name:var(--font-display)]">
                            Launch Pool Progress
                        </p>
                        <p className="text-[16px] font-medium text-white tracking-[-0.32px] leading-[1.15] font-[family-name:var(--font-display)]">
                            {parseFloat(depositCurrent.toFixed(2))} / {parseFloat(depositTarget.toFixed(2))} SOL
                        </p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                        <p className="text-[13px] font-semibold text-[#f3ce28] font-[family-name:var(--font-display)]">
                            Time Left
                        </p>
                        <p className={`text-[16px] font-medium tracking-[-0.32px] leading-[1.15] tabular-nums font-[family-name:var(--font-display)] ${countdown.expired ? "text-red-400 animate-pulse" : "text-white"}`}>
                            {timeDisplay}
                        </p>
                    </div>
                </div>

                {/* Progress bar */}
                <div className="h-2 w-full bg-[rgba(255,255,255,0.08)] rounded-full overflow-hidden">
                    <div
                        className="h-full bg-[#f3ce28] rounded-full transition-all duration-500"
                        style={{ width: `${progress}%` }}
                    />
                </div>

                {/* Footer */}
                <div className="flex justify-between mt-[clamp(8px,1dvh,12px)]">
                    <span className="text-[10px] text-white/40 font-medium font-[family-name:var(--font-display)]">
                        {Math.round(progress)}% Filled
                    </span>
                    <span className="text-[10px] text-white/40 font-medium font-[family-name:var(--font-display)]">
                        Target: {parseFloat(depositTarget.toFixed(2))} SOL
                    </span>
                </div>
            </div>
        </div>
    );
}
